import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getModule, updateModuleProgress, submitQuiz, downloadCertificate } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Award,
  Loader2,
  Clock,
  FileText,
  HelpCircle,
  Download,
  Sprout
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  in_progress: { label: 'In Progress', color: 'bg-sky-100 text-sky-700 border-sky-200' },
  not_started: { label: 'Not Started', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-600 border-red-200' },
  due_soon: { label: 'Due Soon', color: 'bg-amber-100 text-amber-700 border-amber-200' }
};

// Format markdown-style content
const formatContent = (content) => {
  if (!content) return '';
  
  // Convert **text** to bold
  let formatted = content.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Convert bullet points
  formatted = formatted.replace(/^- (.+)$/gm, '<li>$1</li>');
  
  // Wrap consecutive li elements in ul
  formatted = formatted.replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="list-disc pl-5 my-2">${match}</ul>`);
  
  // Convert numbered lists
  formatted = formatted.replace(/^\d+\. (.+)$/gm, '<li>$1</li>');
  
  // Convert double newlines to paragraphs
  formatted = formatted.split('\n\n').map(p => {
    if (p.startsWith('<ul>') || p.startsWith('<li>')) return p;
    return `<p class="mb-3">${p}</p>`;
  }).join('');
  
  return formatted;
};

const TrainingModule = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResults, setQuizResults] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  useEffect(() => {
    const fetchModule = async () => {
      try {
        const response = await getModule(moduleId);
        setModule(response.data);
        
        // If already completed, default to showing certificate tab
        if (response.data.status === 'completed' || response.data.status === 'due_soon') {
          // Keep on content for now
        }
      } catch (err) {
        toast.error('Failed to load training module');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchModule();
  }, [moduleId, navigate]);

  const handleSectionComplete = async (sectionIndex) => {
    try {
      await updateModuleProgress(moduleId, sectionIndex);
      setModule(prev => ({
        ...prev,
        sections_completed: [...(prev.sections_completed || []), sectionIndex],
        status: prev.status === 'not_started' ? 'in_progress' : prev.status
      }));
    } catch (err) {
      console.error('Failed to update progress', err);
    }
  };

  const handleNextSection = () => {
    if (currentSection < module.content_sections.length - 1) {
      handleSectionComplete(currentSection);
      setCurrentSection(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      handleSectionComplete(currentSection);
      setShowQuiz(true);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    if (quizSubmitted) return;
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: answerIndex
    }));
  };

  const handleSubmitQuiz = async () => {
    const answers = Object.entries(quizAnswers).map(([qIndex, aIndex]) => ({
      question_index: parseInt(qIndex),
      selected_answer: aIndex
    }));

    if (answers.length < module.quiz_questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    setSubmittingQuiz(true);
    try {
      const response = await submitQuiz(moduleId, answers);
      setQuizResults(response.data);
      setQuizSubmitted(true);
      
      if (response.data.passed) {
        toast.success('Congratulations! You passed the quiz!');
        // Refresh module data
        const moduleResponse = await getModule(moduleId);
        setModule(moduleResponse.data);
      } else {
        toast.error('You did not pass. You need 80% to pass. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleRetakeQuiz = () => {
    setQuizAnswers({});
    setQuizSubmitted(false);
    setQuizResults(null);
  };

  const handleDownloadCertificate = async () => {
    try {
      await downloadCertificate(moduleId);
      toast.success('Certificate downloaded');
    } catch (err) {
      toast.error('Failed to download certificate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAF7]">
        <Loader2 className="w-8 h-8 animate-spin text-[#5BA4A4]" />
      </div>
    );
  }

  if (!module) return null;

  const config = statusConfig[module.status] || statusConfig.not_started;
  const sectionsCompleted = module.sections_completed?.length || 0;
  const totalSections = module.content_sections?.length || 0;
  const contentProgress = totalSections > 0 ? (sectionsCompleted / totalSections) * 100 : 0;
  const allContentRead = sectionsCompleted >= totalSections;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0FAF7] to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#A8E6CF]/30">
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center h-12 sm:h-16 gap-2 sm:gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" data-testid="back-to-dashboard" className="p-1 sm:p-2 text-[#5BA4A4] hover:bg-[#F0FAF7]">
                <ChevronLeft className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Back</span>
              </Button>
            </Link>
            <div className="flex-1 min-w-0">
              <h1 className="text-sm sm:text-lg font-bold text-[#2D4A4A] truncate">{module.title}</h1>
            </div>
            <Badge variant="outline" className={`${config.color} text-[10px] sm:text-xs flex-shrink-0`}>
              {config.label}
            </Badge>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Module Header */}
        <Card className="border border-[#A8E6CF]/30 mb-4 sm:mb-6 bg-white">
          <CardContent className="p-3 sm:pt-6 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6">
              {module.image_url && (
                <div className="w-full sm:w-48 h-24 sm:h-32 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={module.image_url} 
                    alt={module.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="flex-1">
                <p className="text-xs sm:text-base text-[#5A7A7A] mb-2 sm:mb-4">{module.description}</p>
                
                <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm">
                  <div className="flex items-center gap-1 sm:gap-2 text-[#5A7A7A]">
                    <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                    {totalSections} sections
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 text-[#5A7A7A]">
                    <HelpCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    {module.quiz_questions?.length || 0} questions
                  </div>
                  {module.expires_at && (
                    <div className={`flex items-center gap-1 sm:gap-2 ${
                      module.status === 'expired' ? 'text-red-500' : 
                      module.status === 'due_soon' ? 'text-amber-600' : 'text-[#5A7A7A]'
                    }`}>
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                      {module.status === 'expired' ? 'Exp: ' : ''}
                      {new Date(module.expires_at).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </div>
                  )}
                  {module.quiz_score !== null && module.quiz_score !== undefined && (
                    <div className="flex items-center gap-1 sm:gap-2 text-[#5A7A7A]">
                      <Award className="w-3 h-3 sm:w-4 sm:h-4" />
                      {module.quiz_score}%
                    </div>
                  )}
                </div>

                {(module.status === 'completed' || module.status === 'due_soon') && (
                  <Button 
                    onClick={handleDownloadCertificate}
                    className="mt-3 sm:mt-4 bg-[#5BA4A4] hover:bg-[#4A9090] text-xs sm:text-sm"
                    size="sm"
                    data-testid="download-certificate-button"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    Download Certificate
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="content" className="space-y-4 sm:space-y-6">
          <TabsList className="w-full sm:w-auto bg-[#F0FAF7]">
            <TabsTrigger value="content" data-testid="tab-content" className="text-xs sm:text-sm flex-1 sm:flex-none data-[state=active]:bg-white data-[state=active]:text-[#5BA4A4]">
              <BookOpen className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Training </span>Content
            </TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz" className="text-xs sm:text-sm flex-1 sm:flex-none data-[state=active]:bg-white data-[state=active]:text-[#5BA4A4]">
              <HelpCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Assessment </span>Quiz
            </TabsTrigger>
          </TabsList>

          {/* Content Tab */}
          <TabsContent value="content">
            {!showQuiz ? (
              <Card className="border border-gray-200">
                <CardHeader className="p-3 sm:p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-2 sm:gap-0">
                    <div>
                      <CardTitle className="text-base sm:text-xl">
                        Section {currentSection + 1}: {module.content_sections[currentSection]?.title}
                      </CardTitle>
                      <CardDescription className="mt-1 text-xs sm:text-sm">
                        {currentSection + 1} of {totalSections} sections
                      </CardDescription>
                    </div>
                    <div className="w-full sm:w-auto sm:text-right">
                      <Progress value={(currentSection + 1) / totalSections * 100} className="w-full sm:w-32 h-2" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
                  <div 
                    className="training-content text-sm sm:text-base"
                    dangerouslySetInnerHTML={{ 
                      __html: formatContent(module.content_sections[currentSection]?.content) 
                    }}
                  />

                  <div className="flex justify-between mt-6 sm:mt-8 pt-4 sm:pt-6 border-t gap-2">
                    <Button
                      variant="outline"
                      onClick={handlePrevSection}
                      disabled={currentSection === 0}
                      data-testid="prev-section-button"
                      size="sm"
                      className="text-xs sm:text-sm"
                    >
                      <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Previous</span>
                      <span className="sm:hidden">Prev</span>
                    </Button>
                    <Button
                      onClick={handleNextSection}
                      className="bg-[#5BA4A4] hover:bg-[#4A9090] text-xs sm:text-sm"
                      data-testid="next-section-button"
                      size="sm"
                    >
                      {currentSection === totalSections - 1 ? 'Start Quiz' : 'Next'}
                      <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Alert className="border-sky-200 bg-teal-50">
                <BookOpen className="h-4 w-4 text-sky-600" />
                <AlertTitle className="text-sky-700 text-sm sm:text-base">Content Complete!</AlertTitle>
                <AlertDescription className="text-blue-700 text-xs sm:text-sm">
                  Switch to the Quiz tab to take the assessment.
                </AlertDescription>
              </Alert>
            )}

            {/* Section Navigation */}
            <Card className="border border-gray-200 mt-4 sm:mt-6">
              <CardHeader className="p-3 sm:p-6">
                <CardTitle className="text-base sm:text-lg">Section Overview</CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
                <div className="space-y-1 sm:space-y-2">
                  {module.content_sections.map((section, index) => {
                    const isCompleted = module.sections_completed?.includes(index);
                    const isCurrent = index === currentSection && !showQuiz;
                    
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          setCurrentSection(index);
                          setShowQuiz(false);
                        }}
                        className={`w-full text-left p-2 sm:p-3 rounded-lg flex items-center gap-2 sm:gap-3 transition-colors ${
                          isCurrent 
                            ? 'bg-sky-100 border border-[#A8E6CF]' 
                            : 'hover:bg-gray-100 border border-transparent'
                        }`}
                        data-testid={`section-nav-${index}`}
                      >
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-medium ${
                          isCompleted 
                            ? 'bg-green-100 text-green-700' 
                            : isCurrent 
                            ? 'bg-[#5BA4A4] text-white' 
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          {isCompleted ? <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" /> : index + 1}
                        </div>
                        <span className={`flex-1 text-xs sm:text-sm ${isCurrent ? 'font-medium' : ''}`}>
                          {section.title}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quiz Tab */}
          <TabsContent value="quiz">
            {!allContentRead ? (
              <Card className="border border-amber-200 bg-amber-50">
                <CardContent className="p-6 sm:p-8 text-center">
                  <AlertTriangle className="w-10 h-10 sm:w-12 sm:h-12 text-amber-500 mx-auto mb-3" />
                  <h3 className="text-base sm:text-lg font-bold text-amber-800 mb-2">Content Not Complete</h3>
                  <p className="text-xs sm:text-sm text-amber-700 mb-4">
                    You must read all training content before taking the quiz. You have completed <strong>{sectionsCompleted} of {totalSections}</strong> sections.
                  </p>
                  <Progress value={contentProgress} className="w-48 sm:w-64 h-2 mx-auto mb-4" />
                  <Button
                    onClick={() => {
                      const firstUnread = module.content_sections.findIndex((_, idx) => !module.sections_completed?.includes(idx));
                      setCurrentSection(firstUnread >= 0 ? firstUnread : 0);
                      setShowQuiz(false);
                      const tabTrigger = document.querySelector('[data-testid="tab-content"]');
                      if (tabTrigger) tabTrigger.click();
                    }}
                    className="bg-[#5BA4A4] hover:bg-[#4A9090] text-sm"
                    size="sm"
                    data-testid="go-to-content-btn"
                  >
                    <BookOpen className="w-4 h-4 mr-2" />
                    Continue Reading ({sectionsCompleted}/{totalSections})
                  </Button>
                </CardContent>
              </Card>
            ) : quizSubmitted && quizResults ? (
              <div className="space-y-4 sm:space-y-6">
                {/* Results Summary */}
                <Card className={`border ${quizResults.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                  <CardContent className="p-4 sm:pt-6 sm:p-6">
                    <div className="text-center">
                      {quizResults.passed ? (
                        <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-600 mx-auto mb-3 sm:mb-4" />
                      ) : (
                        <XCircle className="w-12 h-12 sm:w-16 sm:h-16 text-red-600 mx-auto mb-3 sm:mb-4" />
                      )}
                      <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${quizResults.passed ? 'text-green-800' : 'text-red-800'}`}>
                        {quizResults.passed ? 'Congratulations!' : 'Not Quite There'}
                      </h3>
                      <p className={`text-base sm:text-lg mb-3 sm:mb-4 ${quizResults.passed ? 'text-green-700' : 'text-red-700'}`}>
                        {quizResults.score.toFixed(0)}% ({quizResults.correct}/{quizResults.total})
                      </p>
                      <p className={`text-sm ${quizResults.passed ? 'text-green-600' : 'text-red-600'}`}>
                        {quizResults.passed 
                          ? 'Training module completed!' 
                          : '80% needed. Review and try again.'}
                      </p>
                      
                      <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 mt-4 sm:mt-6">
                        {quizResults.passed ? (
                          <>
                            <Button
                              onClick={handleDownloadCertificate}
                              className="bg-[#5BA4A4] hover:bg-[#4A9090] text-sm"
                              size="sm"
                              data-testid="quiz-download-certificate"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Download Certificate
                            </Button>
                            <Link to="/dashboard">
                              <Button variant="outline" size="sm" className="w-full sm:w-auto text-sm">
                                Back to Dashboard
                              </Button>
                            </Link>
                          </>
                        ) : (
                          <Button
                            onClick={handleRetakeQuiz}
                            className="bg-[#5BA4A4] hover:bg-[#4A9090] text-sm"
                            size="sm"
                            data-testid="retake-quiz-button"
                          >
                            Retake Quiz
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Review Answers */}
                <Card className="border border-gray-200">
                  <CardHeader className="p-3 sm:p-6">
                    <CardTitle className="text-base sm:text-lg">Review Your Answers</CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 space-y-4 sm:space-y-6">
                    {module.quiz_questions.map((question, qIndex) => {
                      const result = quizResults.results.find(r => r.question_index === qIndex);
                      const userAnswer = result?.selected;
                      const correctAnswer = result?.correct;
                      const isCorrect = result?.is_correct;

                      return (
                        <div key={qIndex} className="border-b pb-4 sm:pb-6 last:border-0">
                          <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4">
                            {isCorrect ? (
                              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            )}
                            <p className="font-medium text-sm sm:text-base text-[#2D4A4A]">
                              {qIndex + 1}. {question.question}
                            </p>
                          </div>
                          <div className="space-y-1 sm:space-y-2 ml-6 sm:ml-8">
                            {question.options.map((option, oIndex) => (
                              <div
                                key={oIndex}
                                className={`p-2 sm:p-3 rounded-lg border text-xs sm:text-sm ${
                                  oIndex === correctAnswer
                                    ? 'border-green-500 bg-green-50'
                                    : oIndex === userAnswer && !isCorrect
                                    ? 'border-red-500 bg-red-50'
                                    : 'border-gray-200'
                                }`}
                              >
                                <span className={
                                  oIndex === correctAnswer
                                    ? 'text-green-800'
                                    : oIndex === userAnswer && !isCorrect
                                    ? 'text-red-800'
                                    : 'text-gray-700'
                                }>
                                  {option}
                                </span>
                                {oIndex === correctAnswer && (
                                  <span className="ml-1 sm:ml-2 text-green-600 text-[10px] sm:text-sm">(Correct)</span>
                                )}
                                {oIndex === userAnswer && !isCorrect && (
                                  <span className="ml-1 sm:ml-2 text-red-600 text-[10px] sm:text-sm">(Your answer)</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="border border-gray-200">
                <CardHeader className="p-3 sm:p-6">
                  <CardTitle className="text-base sm:text-lg">Assessment Quiz</CardTitle>
                  <CardDescription className="text-xs sm:text-sm">
                    Answer all questions. 80% to pass.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0 space-y-4 sm:space-y-8">
                  {module.quiz_questions.map((question, qIndex) => (
                    <div key={qIndex} className="pb-4 sm:pb-6 border-b last:border-0">
                      <p className="font-medium text-sm sm:text-base text-[#2D4A4A] mb-3 sm:mb-4">
                        {qIndex + 1}. {question.question}
                      </p>
                      <div className="space-y-1 sm:space-y-2">
                        {question.options.map((option, oIndex) => (
                          <button
                            key={oIndex}
                            onClick={() => handleAnswerSelect(qIndex, oIndex)}
                            className={`quiz-option w-full text-left text-xs sm:text-sm p-2 sm:p-4 ${
                              quizAnswers[qIndex] === oIndex ? 'selected' : ''
                            }`}
                            data-testid={`quiz-option-${qIndex}-${oIndex}`}
                          >
                            <span className="flex items-center gap-2 sm:gap-3">
                              <span className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                quizAnswers[qIndex] === oIndex 
                                  ? 'border-[#5BA4A4] bg-[#5BA4A4] text-white' 
                                  : 'border-gray-300'
                              }`}>
                                {quizAnswers[qIndex] === oIndex && (
                                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                                )}
                              </span>
                              {option}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pt-4 border-t gap-3 sm:gap-0">
                    <p className="text-xs sm:text-sm text-[#5A7A7A]">
                      {Object.keys(quizAnswers).length}/{module.quiz_questions.length} answered
                    </p>
                    <Button
                      onClick={handleSubmitQuiz}
                      disabled={submittingQuiz || Object.keys(quizAnswers).length < module.quiz_questions.length}
                      className="bg-[#5BA4A4] hover:bg-[#4A9090] w-full sm:w-auto text-sm"
                      size="sm"
                      data-testid="submit-quiz-button"
                    >
                      {submittingQuiz ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Quiz'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default TrainingModule;
